import { getSession, clearSession } from './storage';

const errorDetail = (detail) => Array.isArray(detail)
  ? detail.map((item) => item.msg).join('; ')
  : detail;

// Use relative URL for same-origin requests (recommended for production with reverse proxy)
// Falls back to direct backend URL for local development
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Generic fetch wrapper with error handling
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} options - Fetch options
 * @param {Function} options.onUploadProgress - Optional callback for upload progress (percent: number)
 * @returns {Promise} Response data
 */
const apiFetch = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const { onUploadProgress, ...fetchOptions } = options;
  const token = getSession()?.access_token;
  if (token) {
    fetchOptions.headers = { ...fetchOptions.headers, Authorization: `Bearer ${token}` };
  }
  const checkAuthentication = (status) => {
    if (status === 401 && endpoint.startsWith('/admin/')) {
      clearSession();
      window.dispatchEvent(new Event('auth-expired'));
    }
  };

  try {
    // Use XMLHttpRequest for upload progress tracking if callback provided
    if (onUploadProgress && fetchOptions.body instanceof FormData) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Track upload progress
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onUploadProgress(percentComplete);
          }
        });

        // Handle completion
        xhr.addEventListener('load', () => {
          const contentType = xhr.getResponseHeader('content-type');

          if (xhr.status >= 200 && xhr.status < 300) {
            // Parse response based on content type
            if (contentType && contentType.includes('application/json')) {
              try {
                resolve(JSON.parse(xhr.responseText));
              } catch (e) {
                resolve(xhr.responseText);
              }
            } else {
              resolve(xhr.response);
            }
          } else {
            checkAuthentication(xhr.status);
            // Handle error
            let errorMessage = `HTTP error! status: ${xhr.status}`;
            if (contentType && contentType.includes('application/json')) {
              try {
                const errorData = JSON.parse(xhr.responseText);
                errorMessage = errorDetail(errorData.detail) || errorMessage;
              } catch (e) {
                // Ignore parse error
              }
            }
            reject(new Error(errorMessage));
          }
        });

        // Handle network errors
        xhr.addEventListener('error', () => {
          reject(new Error('Network error occurred during upload'));
        });

        // Handle timeout
        xhr.addEventListener('timeout', () => {
          reject(new Error('Upload timed out'));
        });

        // Open and send request
        xhr.open(fetchOptions.method || 'POST', url);

        // Set custom headers (but not Content-Type for FormData - browser handles it)
        if (fetchOptions.headers) {
          Object.entries(fetchOptions.headers).forEach(([key, value]) => {
            if (key.toLowerCase() !== 'content-type') {
              xhr.setRequestHeader(key, value);
            }
          });
        }

        // Set timeout to 15 minutes for large uploads
        xhr.timeout = 900000; // 15 minutes

        xhr.send(fetchOptions.body);
      });
    }

    // Use standard fetch for non-progress-tracked requests
    const response = await fetch(url, {
      ...fetchOptions,
      headers: {
        ...fetchOptions.headers,
      },
    });

    // Handle non-JSON responses (like file downloads)
    const contentType = response.headers.get('content-type');

    if (!response.ok) {
      checkAuthentication(response.status);
      // Try to parse error message
      let errorMessage = `HTTP error! status: ${response.status}`;

      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        errorMessage = errorDetail(errorData.detail) || errorMessage;
      }

      throw new Error(errorMessage);
    }

    // Return blob for file downloads
    if (contentType && (contentType.includes('video') || contentType.includes('audio'))) {
      return response.blob();
    }

    // Return JSON for API responses
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return response;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

/**
 * Authentication API
 */
export const authAPI = {
  login: (email, password) => {
    return apiFetch('/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });
  },
};

/**
 * Admin API
 */
export const adminAPI = {
  getEvaluationConfig: () => apiFetch('/admin/evaluation-lab/config'),
  createEvaluationRun: (data) => apiFetch('/admin/evaluation-lab/runs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }),
  getEvaluationRuns: (recordingId, offset = 0) => apiFetch(`/admin/evaluation-lab/runs?recording_id=${recordingId}&limit=20&offset=${offset}`),
  getEvaluationRun: (id) => apiFetch(`/admin/evaluation-lab/runs/${id}`),
  // Assessments
  createAssessment: (formData) => {
    return apiFetch('/admin/assessments', {
      method: 'POST',
      body: formData, // FormData for file upload
    });
  },

  getAssessments: () => {
    return apiFetch('/admin/assessments');
  },

  getDefaultSystemPrompt: () => {
    return apiFetch('/admin/assessments/default-prompt');
  },

  updateAssessment: (assessmentId, data) => {
    return apiFetch(`/admin/assessments/${assessmentId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  },

  // Questions
  getAssessmentQuestions: (assessmentId) => {
    return apiFetch(`/admin/assessments/${assessmentId}/questions`);
  },

  updateQuestion: (questionId, questionText) => {
    return apiFetch(`/admin/questions/${questionId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ question_text: questionText }),
    });
  },

  // Candidates
  createCandidate: (email) => {
    return apiFetch('/admin/candidates', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
  },

  getCandidates: () => {
    return apiFetch('/admin/candidates');
  },

  // Assignments
  createAssignments: (candidateId, assessmentIds) => {
    return apiFetch('/admin/assignments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        candidate_id: candidateId,
        assessment_ids: assessmentIds,
      }),
    });
  },

  // Grading
  getCandidatesForGrading: () => {
    return apiFetch('/admin/grading/candidates');
  },

  getCandidateRecordings: (candidateId) => {
    return apiFetch(`/admin/grading/candidate/${candidateId}`);
  },

  submitGrade: (recordingId, scorePercentage, feedback) => {
    return apiFetch(`/admin/grading/recording/${recordingId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        score_percentage: scorePercentage,
        feedback: feedback || null,
      }),
    });
  },

  triggerAutoGrade: (recordingId) => {
    return apiFetch(`/admin/grading/recording/${recordingId}/auto-grade`, {
      method: 'POST',
    });
  },

  resetRecording: (recordingId) => {
    return apiFetch(`/admin/grading/recording/${recordingId}/reset`, {
      method: 'DELETE',
    });
  },

  // Viewer Management
  createViewer: (email) => {
    return apiFetch('/admin/viewers', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
  },

  getViewers: () => {
    return apiFetch('/admin/viewers');
  },

  createViewerAssignments: (viewerId, candidateIds) => {
    return apiFetch('/admin/viewer-assignments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        viewer_id: viewerId,
        candidate_ids: candidateIds,
      }),
    });
  },

  getViewerAssignments: (viewerId) => {
    return apiFetch(`/admin/viewer-assignments/${viewerId}`);
  },

  createViewerAssignmentsBulk: (viewerId, assignments) => {
    return apiFetch('/admin/viewer-assignments-bulk', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        viewer_id: viewerId,
        assignments: assignments,
      }),
    });
  },

  updateViewerAssignmentAccess: (assignmentId, fullAccess) => {
    return apiFetch(`/admin/viewer-assignments/${assignmentId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        full_access: fullAccess,
      }),
    });
  },
};

/**
 * Viewer API
 */
export const viewerAPI = {
  getAssignedCandidates: (viewerId) => {
    return apiFetch(`/viewer/candidates?viewer_id=${viewerId}`);
  },

  getCandidateRecordings: (candidateId, viewerId) => {
    return apiFetch(`/viewer/grading/candidate/${candidateId}?viewer_id=${viewerId}`);
  },
};

/**
 * Candidate API
 */
export const candidateAPI = {
  getAssignments: (candidateId) => {
    return apiFetch(`/candidate/assignments?candidate_id=${candidateId}`);
  },

  getAssignmentQuestions: (assignmentId) => {
    return apiFetch(`/candidate/assignment/${assignmentId}/questions`);
  },

  startQuestion: (questionId, assignmentId, screenShareActive) => {
    return apiFetch(`/candidate/question/${questionId}/start?assignment_id=${assignmentId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ screen_share_active: screenShareActive }),
    });
  },

  uploadRecording: (formData, onUploadProgress) => {
    return apiFetch('/candidate/recording', {
      method: 'POST',
      body: formData, // FormData with recordings
      onUploadProgress: onUploadProgress, // Progress callback
    });
  },

  // Test recording endpoints
  startTestQuestion: (assignmentId, screenShareActive) => {
    return apiFetch(`/candidate/assignment/${assignmentId}/test-question/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ screen_share_active: screenShareActive }),
    });
  },

  uploadTestRecording: (formData, onUploadProgress) => {
    return apiFetch('/candidate/test-recording', {
      method: 'POST',
      body: formData, // FormData with test recordings
      onUploadProgress: onUploadProgress, // Progress callback
    });
  },

  getTestRecordingStatus: (assignmentId) => {
    return apiFetch(`/candidate/assignment/${assignmentId}/test-recording/status`);
  },
};

/**
 * Recording API
 */
export const recordingAPI = {
  getScreenRecording: (recordingId) => {
    return `${API_BASE_URL}/recordings/${recordingId}/screen`;
  },

  getWebcamRecording: (recordingId) => {
    return `${API_BASE_URL}/recordings/${recordingId}/webcam`;
  },

  getAudioRecording: (recordingId) => {
    return `${API_BASE_URL}/recordings/${recordingId}/audio`;
  },
};
